import React from 'react';
import { Check, X, Minus, AlertCircle, MessageSquare, Award, Flame } from 'lucide-react';
import { formatPosition, BJJ_FELT_SCALE } from '../../data/combatives';

/**
 * BJJSessionDetail — read-only display of a single saved BJJ journal
 * session. Rendered inside the expanded section of SessionCard when the
 * session is from BJJ (or has the journal shape). Anti-shame copy
 * throughout — labels emphasize what the user did, not what they missed.
 *
 * Props:
 *   session — full saved session record (see BJJLogDialog handleSave)
 */
const BJJSessionDetail = ({ session }) => {
  const hasJournal = !!(
    session.intent ||
    (session.techniques && session.techniques.length > 0) ||
    (session.focusModes && session.focusModes.length > 0) ||
    session.sparring ||
    session.reflection ||
    session.injury
  );

  if (!hasJournal) return null;

  return (
    <div className="space-y-5 mt-4">
      {/* Header chips — class format, focus modes, coaches */}
      {(session.classFormat?.length || session.focusModes?.length || session.coaches?.length) ? (
        <div className="space-y-2">
          {session.classFormat?.length > 0 && (
            <ChipList label="Class format" items={session.classFormat} />
          )}
          {session.focusModes?.length > 0 && (
            <ChipList label="Focus" items={session.focusModes} />
          )}
          {session.coaches?.length > 0 && (
            <ChipList label="Coaches" items={session.coaches} />
          )}
        </div>
      ) : null}

      {/* Intent + outcome */}
      {session.intent && (
        <div className="p-3 rounded-md bg-enf-accent/10 border border-enf-accent-fg/30">
          <div className="text-[10px] uppercase tracking-widest text-enf-accent-fg font-semibold mb-1">Intent</div>
          <div className="text-sm text-enf-text-primary italic mb-1.5">&ldquo;{session.intent}&rdquo;</div>
          {session.reflection?.intentHit !== undefined && session.reflection?.intentHit !== null && (
            <div className="flex items-center gap-1.5 text-xs">
              <IntentOutcomeIcon outcome={session.reflection.intentHit} />
              <span className="text-enf-text-secondary">
                {session.reflection.intentHit === true ? 'Accomplished'
                  : session.reflection.intentHit === 'partial' ? 'Partially'
                  : 'Not yet — next time'}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Techniques */}
      {session.techniques?.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-semibold mb-2">
            Techniques · {session.techniques.length}
          </div>
          <div className="space-y-2">
            {session.techniques.map((t, i) => (
              <TechniqueDetailCard key={i} tech={t} />
            ))}
          </div>
        </div>
      )}

      {/* Sparring */}
      {session.sparring && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-semibold mb-2">Sparring</div>
          <SparringDetail s={session.sparring} />
        </div>
      )}

      {/* Reflection (win / gap / nextFocus only — intent rendered above) */}
      {session.reflection && (session.reflection.win || session.reflection.gap || session.reflection.nextFocus) && (
        <div>
          <div className="text-[11px] uppercase tracking-[0.25em] text-enf-role-info font-semibold mb-2">Reflection</div>
          <div className="space-y-2">
            {session.reflection.win && <LabeledBlock label="Win" body={session.reflection.win} />}
            {session.reflection.gap && <LabeledBlock label="Gap" body={session.reflection.gap} />}
            {session.reflection.nextFocus && <LabeledBlock label="Next focus" body={session.reflection.nextFocus} />}
          </div>
        </div>
      )}

      {/* Injury */}
      {session.injury && (session.injury.bodyPart || session.injury.note) && (
        <div className="p-3 rounded-md bg-enf-role-warning/5 border border-enf-role-warning/30">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-enf-role-warning font-semibold mb-1">
            <AlertCircle className="w-3 h-3" /> Injury logged
          </div>
          {session.injury.bodyPart && <div className="text-sm text-enf-text-primary font-semibold">{session.injury.bodyPart}</div>}
          {session.injury.note && <div className="text-sm text-enf-text-secondary mt-0.5">{session.injury.note}</div>}
        </div>
      )}

      {/* Belt snapshot at time of log */}
      {session.beltSnapshot?.rankId && (
        <div className="flex items-center gap-1.5 text-xs text-enf-text-muted">
          <Award className="w-3 h-3" />
          <span>Belt at the time: {session.beltSnapshot.rankId}{session.beltSnapshot.stripes ? ` · ${session.beltSnapshot.stripes} stripe${session.beltSnapshot.stripes > 1 ? 's' : ''}` : ''}</span>
        </div>
      )}
    </div>
  );
};

// ---------- Sub-components ----------

const ChipList = ({ label, items }) => (
  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
    <span className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold flex-shrink-0">{label}</span>
    {items.map((it) => (
      <span key={it} className="inline-flex items-center px-1.5 py-0.5 rounded bg-enf-surface-mid border border-enf-surface-border text-xs text-enf-text-secondary">
        {it}
      </span>
    ))}
  </div>
);

const IntentOutcomeIcon = ({ outcome }) => {
  if (outcome === true) return <Check className="w-3.5 h-3.5 text-enf-role-info" />;
  if (outcome === false) return <Minus className="w-3.5 h-3.5 text-enf-text-muted" />;
  return <Minus className="w-3.5 h-3.5 text-enf-role-warning" />; // partial
};

const LabeledBlock = ({ label, body }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-enf-text-muted font-semibold mb-0.5">{label}</div>
    <div className="text-sm text-enf-text-primary whitespace-pre-wrap">{body}</div>
  </div>
);

const TechniqueDetailCard = ({ tech }) => {
  const positionLabel = formatPosition(tech.position);
  const isWeapons = tech.weapon && tech.weapon !== '';
  return (
    <div className="p-3 rounded-md bg-enf-surface-mid border border-enf-surface-border">
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-enf-text-primary">
            {tech.name || '(unnamed technique)'}
          </div>
          {positionLabel && (
            <div className="text-xs text-enf-text-muted mt-0.5">{positionLabel}</div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1 flex-shrink-0">
          {tech.mastery && (
            <span className="px-1.5 py-0.5 rounded bg-enf-surface-deep border border-enf-surface-border font-mono text-[10px] text-enf-text-secondary">
              {tech.mastery}
            </span>
          )}
          {tech.reps && (
            <span className="px-1.5 py-0.5 rounded bg-enf-surface-deep border border-enf-surface-border font-mono text-[10px] text-enf-text-secondary">
              {tech.reps} reps
            </span>
          )}
        </div>
      </div>
      {tech.submission && (
        <div className="text-xs text-enf-role-info mb-1.5">
          <span className="text-enf-text-muted">Sub:</span> {tech.submission}
        </div>
      )}
      {isWeapons && (
        <div className="flex items-center gap-1.5 text-xs text-enf-role-warning mb-1.5">
          <Flame className="w-3 h-3" />
          <span>{tech.weapon}{tech.weaponMode ? ` · ${tech.weaponMode}` : ''}</span>
        </div>
      )}
      {tech.steps?.length > 0 && (
        <ol className="space-y-1 mt-2">
          {tech.steps.map((step, i) => (
            <li key={i} className="flex items-start gap-2 text-xs">
              <span className="font-mono text-enf-text-muted flex-shrink-0 w-4">{i + 1}.</span>
              <span className={`flex-1 ${step.needsDrilling ? 'text-enf-text-primary' : 'text-enf-text-secondary'}`}>
                {step.text || '(blank step)'}
              </span>
              {step.needsDrilling && (
                <span className="px-1 py-0.5 rounded bg-enf-role-warning/15 border border-enf-role-warning/40 text-enf-role-warning text-[9px] font-semibold uppercase tracking-wider flex-shrink-0">
                  Drilling
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
      {tech.detail && (
        <div className="mt-2 pt-2 border-t border-enf-surface-border">
          <div className="flex items-start gap-1.5 text-xs">
            <MessageSquare className="w-3 h-3 text-enf-text-muted mt-0.5 flex-shrink-0" />
            <p className="text-enf-text-secondary whitespace-pre-wrap">{tech.detail}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const SparringDetail = ({ s }) => {
  const felt = BJJ_FELT_SCALE.find((f) => f.value === s.feltScale);
  // Render only the stats that aren't 0 — keeps the read view clean.
  const stats = [
    ['Subs landed', s.subsLanded],
    ['Subs caught in', s.subsCaught],
    ['Sweeps landed', s.sweepsLanded],
    ['Sweeps allowed', s.sweepsAllowed],
    ['Passes landed', s.passesLanded],
    ['Passes allowed', s.passesAllowed],
    ['Takedowns landed', s.takedownsLanded],
    ['Takedowns conceded', s.takedownsConceded],
  ].filter(([, v]) => v > 0);

  return (
    <div className="p-3 rounded-md bg-enf-surface-mid border border-enf-surface-border space-y-2">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {s.rolls > 0 && (
          <span className="text-enf-text-primary"><span className="text-enf-text-muted text-xs uppercase tracking-widest mr-1">Rolls</span>{s.rolls}</span>
        )}
        {felt && (
          <span className="text-enf-text-primary"><span className="text-enf-text-muted text-xs uppercase tracking-widest mr-1">Felt</span>{felt.label}</span>
        )}
      </div>
      {s.partners?.length > 0 && (
        <ChipList label="Partners" items={s.partners} />
      )}
      {stats.length > 0 && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs pt-1">
          {stats.map(([label, v]) => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-enf-text-muted">{label}</span>
              <span className="font-mono text-enf-text-primary">{v}</span>
            </div>
          ))}
        </div>
      )}
      {s.note && (
        <div className="text-xs text-enf-text-secondary whitespace-pre-wrap pt-1 border-t border-enf-surface-border">
          {s.note}
        </div>
      )}
    </div>
  );
};

export default BJJSessionDetail;
